import { DIRECTIVE_HOOKS } from '@convenia/utils/modules/vue3Migration'

/*
  The tooltip custom directive can be used with the following options:

  - No modifiers, no binding value ( <component v-tooltip.ellipsis /> )
    [ The tooltip will appear when the content is being hidden with its default
    styles, displaying the full content. ]

  - Dark modifier ( <component v-tooltip.dark /> )
    [ The tooltip will appear with its dark theme style ]

  - Binding value ( <component v-tooltip="'Some content'" /> )
    [ The tooltip will appear with the content set in the binding ]

  - Tooltip distance as argument ( <component v-tooltip:10.dark /> )
    [ The tooltip will appear distance of 10px from the element ]

  - Object as binding value ( <component v-tooltip=" {
      value: [String] The text to be displayed in the tooltip,
      position: [String] 'top' of 'bottom',
      align: [String] 'left', 'right' or 'center',
      fontSize: [Number] default 11,
      maxWidth: [Number] default 200,
      horiziontalPadding: [Number] default 10
      hide: [Boolean] Whether to hide the tooltip,
      parentEl: [Element] The parent of the tooltip element other than the one
      which has the declared directive
    }" /> )

    =====================================================================

    This directive, when called with the '.ellipsis' modifier,
    is meant to be used in situations that content is being hidden
    by the css attribute 'text-overflow: ellipsis'.

    The html element must use the following css attributes for the desired effect:

    overflow-x: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;

    =====================================================================

    This directive, when called with the '.lineClamp:x' modifier,
    is meant to be used in situations where content needs to be truncated
    after a specific number of lines.

    The `x` in `.lineClamp:x` represents the number of lines to display before truncating.
    If no value is provided, it defaults to 1 line.

    The HTML element must use the following CSS attributes for the desired effect:

    display: -webkit-box;
    -webkit-line-clamp: [x]; // Number of lines to display
    -webkit-box-orient: vertical;
    overflow: hidden;

    Example usage:
    - <component v-tooltip.lineClamp:3 />
      [ The tooltip will appear when the content is truncated after 3 lines. ]

    - <component v-tooltip.lineClamp />
      [ The tooltip will appear when the content is truncated after 1 line (default). ]
*/
import createTooltip from './createTooltip'

const directive = (() => {
  const state = new WeakMap()

  return {
    [DIRECTIVE_HOOKS.BEFORE_MOUNT] (el, binding) {
      const bindingValue = (binding || {}).value || {}
      const isObject = typeof binding.value === 'object'
      const align = (isObject && binding.value.align) || 'center'
      const value = binding.modifiers.ellipsis
        ? el.innerText
        : isObject ? binding.value.value : binding.value

      const { tooltip, setShow, appUnmount } = createTooltip({
        bindingValue,
        isObject,
        align,
        el,
        binding,
        value
      })

      const parentEl = isObject
        ? bindingValue.parentEl || document.body
        : document.body

      parentEl.appendChild(tooltip.$el)

      const handleMouseLeave = () => {
        setShow(false)
      }

      const handleScroll = handleMouseLeave
      const showTooltip = () => {
        setShow(true)
        document.addEventListener(
          'scroll',
          handleScroll,
          { capture: true, once: true }
        )
      }

      const hasLineClamp =
        Object.keys((binding || {}).modifiers || {}).some(key => key.startsWith('line-clamp'))

      const getLineClampValue = () => {
        const lineClampModifiers = Object.keys(binding.modifiers).filter(key => key.startsWith('line-clamp'))
        const lineClampValues = lineClampModifiers.map(key => key.replace('line-clamp:', ''))

        return lineClampValues
      }

      const lineClampStyles = {
        display: '-webkit-box',
        '-webkit-line-clamp': getLineClampValue() || 1,
        '-webkit-box-orient': 'vertical',
        overflow: 'hidden'
      }

      const truncateStyles = {
        overflow: 'hidden',
        'max-width': (bindingValue || {}).truncateWidth || '100%',
        'white-space': 'nowrap',
        'text-overflow': 'ellipsis',
      }

      if (((binding || {}).modifiers || {}).ellipsis && el) {
        el.style.cssText += Object.entries(truncateStyles)
          .map(([ key, value ]) => (value ? `${key}:${value}` : ''))
          .join(';')
      }

      if (hasLineClamp && el) {
        el.style.cssText += Object.entries(lineClampStyles)
          .map(([ key, value ]) => (value ? `${key}:${value}` : ''))
          .join(';')
      }

      const handleMouseEnter = () => {
        const isEllipsis = binding.modifiers.ellipsis
        const isLineClamp = hasLineClamp

        const hasHiddenContent = isEllipsis
          ? el.clientWidth < el.scrollWidth
          : isLineClamp && el.scrollHeight > el.clientHeight

        if (isEllipsis || isLineClamp) hasHiddenContent && showTooltip()
        else showTooltip()
      }

      el.addEventListener('mouseenter', handleMouseEnter)
      el.addEventListener('mouseleave', handleMouseLeave)
      state.set(el, {
        tooltip,
        handleMouseEnter,
        handleMouseLeave,
        setShow,
        appUnmount
      })
    },

    [DIRECTIVE_HOOKS.UPDATED] (el, binding) {
      const isObject = typeof binding.value === 'object'
      const value = isObject ? binding.value.value : binding.value
      const oldValue = isObject ? binding.oldValue.value : binding.oldValue
      const { tooltip, setShow } = state.get(el) || {}

      if (!tooltip) return

      if (value !== oldValue) tooltip.value = value
      tooltip.hide = (binding.value || {}).hide
      // If hide is set to true, reset the show value
      if (tooltip.hide) setShow(false)
    },

    [DIRECTIVE_HOOKS.UNMOUNTED] (el) {
      const { tooltip, handleMouseEnter, handleMouseLeave, appUnmount } = state.get(el) || {}
      el.removeEventListener('mouseenter', handleMouseEnter)
      el.removeEventListener('mouseleave', handleMouseLeave)
      document.removeEventListener('scroll', handleMouseLeave, { capture: true })

      const tooltipEl = (tooltip || {}).$el
      if (!tooltipEl) return

      tooltipEl.remove()
      appUnmount()
      state.delete(el)
    }
  }
})()

export default directive
